import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { paymentService } from '../services/paymentService';

const PaymentResult = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const registrationId = searchParams.get('registrationId');
  const hostedCheckoutId = searchParams.get('hostedCheckoutId');

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        let checkoutId = hostedCheckoutId;
        
        // If no hostedCheckoutId in URL, try to get from sessionStorage
        if (!checkoutId) {
          const pendingRegistration = sessionStorage.getItem('pendingRegistration');
          if (pendingRegistration) {
            const data = JSON.parse(pendingRegistration);
            checkoutId = data.hostedCheckoutId;
          }
        }

        if (!checkoutId) {
          throw new Error('No hosted checkout ID found');
        }

        await fetchPaymentDetailsById(checkoutId);
      } catch (error) {
        console.error('Error fetching payment details:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (registrationId) {
      fetchPaymentDetails();
    } else {
      // Try to get from sessionStorage
      const pendingRegistration = sessionStorage.getItem('pendingRegistration');
      if (pendingRegistration) {
        const data = JSON.parse(pendingRegistration);
        if (data.hostedCheckoutId) {
          fetchPaymentDetailsById(data.hostedCheckoutId);
        }
      } else {
        setError('No payment information found');
        setLoading(false);
      }
    }
  }, [registrationId, hostedCheckoutId]);


  const fetchPaymentDetailsById = async (checkoutId) => {
    const response = await paymentService.getHostedCheckoutStatus(checkoutId);
    setPaymentDetails(response);

    // Clear sessionStorage after successful fetch
    sessionStorage.removeItem('pendingRegistration');

    // Show appropriate toast message
    if (response.success && response.payment) {
      const paymentStatus = response.payment.status;
      if (paymentStatus === 'CAPTURED' || paymentStatus === 'PAID') {
        toast.success('Payment completed successfully!');
      } else if (paymentStatus === 'PENDING_APPROVAL' || paymentStatus === 'PENDING_COMPLETION') {
        toast.info('Payment is being processed');
      } else if (paymentStatus === 'REJECTED' || paymentStatus === 'CANCELLED') {
        toast.error('Payment failed or was cancelled');
      }
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'CAPTURED':
      case 'PAID':
        return 'success';
      case 'PENDING_APPROVAL':
      case 'PENDING_COMPLETION':
        return 'warning';
      case 'REJECTED':
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'CAPTURED':
      case 'PAID':
        return 'Payment Successful';
      case 'PENDING_APPROVAL':
      case 'PENDING_COMPLETION':
        return 'Payment Pending';
      case 'REJECTED':
        return 'Payment Rejected';
      case 'CANCELLED':
        return 'Payment Cancelled';
      default:
        return status || 'Unknown Status';
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleViewRegistrations = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <Card className="shadow">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading payment details...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <Card className="shadow">
          <Card.Body className="text-center py-5">
            <Alert variant="danger">
              <h5>Error Loading Payment Details</h5>
              <p>{error}</p>
              <Button variant="primary" onClick={handleBackToHome}>
                Back to Home
              </Button>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <Card className="shadow">
        <Card.Header className="bg-light">
          <h3 className="mb-0">Payment Result</h3>
        </Card.Header>
        <Card.Body>
          {paymentDetails && (
            <div>
              <div className="text-center mb-4">
                <Badge 
                  bg={getStatusVariant(paymentDetails.payment?.status)} 
                  className="fs-6 p-3"
                >
                  {getStatusText(paymentDetails.payment?.status)}
                </Badge>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <h6>Payment Information</h6>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td><strong>Hosted Checkout ID:</strong></td>
                        <td>{paymentDetails.hostedCheckoutId}</td>
                      </tr>
                      {paymentDetails.payment && (
                        <>
                          <tr>
                            <td><strong>Payment ID:</strong></td>
                            <td>{paymentDetails.payment.id}</td>
                          </tr>
                          <tr>
                            <td><strong>Status:</strong></td>
                            <td>
                              <Badge bg={getStatusVariant(paymentDetails.payment.status)}>
                                {paymentDetails.payment.status}
                              </Badge>
                            </td>
                          </tr>
                        </>
                      )}
                      {registrationId && (
                        <tr>
                          <td><strong>Registration ID:</strong></td>
                          <td>{registrationId}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {paymentDetails.payment?.paymentOutput && (
                  <div className="col-md-6">
                    <h6>Transaction Details</h6>
                    <table className="table table-borderless">
                      <tbody>
                        {paymentDetails.payment.paymentOutput.amountOfMoney && (
                          <tr>
                            <td><strong>Amount:</strong></td>
                            <td>
                              {paymentDetails.payment.paymentOutput.amountOfMoney.currencyCode} {
                                (paymentDetails.payment.paymentOutput.amountOfMoney.amount / 100).toFixed(2)
                              }
                            </td>
                          </tr>
                        )}
                        {paymentDetails.payment.paymentOutput.references?.merchantReference && (
                          <tr>
                            <td><strong>Reference:</strong></td>
                            <td>{paymentDetails.payment.paymentOutput.references.merchantReference}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {paymentDetails.payment?.status === 'CAPTURED' || paymentDetails.payment?.status === 'PAID' ? (
                <Alert variant="success" className="mt-4">
                  <h6>Registration Confirmed!</h6>
                  <p className="mb-0">
                    Your payment has been processed successfully. Your registration for NSDSST 2025 is now confirmed.
                    You will receive a confirmation email shortly.
                  </p>
                </Alert>
              ) : paymentDetails.payment?.status === 'PENDING_APPROVAL' || paymentDetails.payment?.status === 'PENDING_COMPLETION' ? (
                <Alert variant="warning" className="mt-4">
                  <h6>Payment Processing</h6>
                  <p className="mb-0">
                    Your payment is being processed. Please allow some time for the transaction to complete.
                    You will be notified once the payment is confirmed.
                  </p>
                </Alert>
              ) : (
                <Alert variant="danger" className="mt-4">
                  <h6>Payment Issue</h6>
                  <p className="mb-0">
                    There was an issue with your payment. Please try again or contact support if the problem persists.
                  </p>
                </Alert>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Button variant="secondary" onClick={handleBackToHome}>
                  Back to Home
                </Button>
                <Button variant="primary" onClick={handleViewRegistrations}>
                  View All Registrations
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaymentResult;
